import { Dexie } from 'dexie'
import { track } from '@opallabs/api/v1'
import { IndexedDBSchema } from './types'

const DATABASE_NAME = 'opal_database'
const TRACKING_EVENT = 'IndexedDB_Database_Upgade'
const indexedDB = new Dexie(DATABASE_NAME) as Dexie & IndexedDBSchema

/**
 * Fires off a tracking API call with the upgraded database version and name.
 */
const trackDBUpgrade = (version: number) => {
  track.create({
    event: TRACKING_EVENT,
    metadata: { database: DATABASE_NAME, version },
  })
}

/**
 * !!!IMPORTANT!!! As the store changes and version needs to be incremented, we
 * will continue to add newly declared versions to this file. Its VERY IMPORTANT
 * to follow dexie's versioning guide to prevent breaking changes for the user.
 *
 * @see https://dexie.org/docs/Tutorial/Design#database-versioning
 */
indexedDB
  .version(1)
  .stores({
    userPlanPreferences: '++id, [planId+userId]',
  } satisfies Partial<Record<keyof IndexedDBSchema, string>>)
  /**
   * An upgrade callback that can hold code needed to migrate from one schema to another. We will also use these upgrade
   * functions to track the versions of `opal_database` existing out in the while - the segment event created will hold
   * the user's UUID as well as all of the versions they have upgraded to. This will help us keep tabs on what versions
   * exist in our users browsers.
   *
   * @note - This upgrade function won't ever actually run because it is the first version and there is no upgrade step.
   * It is just here as an example for future versions.
   */
  .upgrade(() => {
    trackDBUpgrade(1)
  })

indexedDB
  .version(2)
  .stores({
    userPlanPreferences: '++id, [userId+planId]',
  } satisfies Partial<Record<keyof IndexedDBSchema, string>>)
  .upgrade(() => {
    // Changelog: order of compound index on userPlanPreferences was reversed.
    // No data migrations needed.
    trackDBUpgrade(2)
  })

indexedDB
  .version(3)
  .stores({
    userCategoryPreferences: '++id, [userId+planId+categoryId]',
  } satisfies Partial<Record<keyof IndexedDBSchema, string>>)
  .upgrade(() => {
    // Changelog: userCategoryPreferences table added.
    // No data migrations needed.
    trackDBUpgrade(3)
  })

indexedDB
  .version(4)
  .stores({
    pendingBulkRequests: '++id, [operationId]',
  } satisfies Partial<Record<keyof IndexedDBSchema, string>>)
  .upgrade(() => {
    // Changelog: pendingBulkRequest table added.
    // No data migrations needed.
    trackDBUpgrade(4)
  })

export { indexedDB }
